"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TypeForm = exports.TypeFormModal = exports.TypeFormDescriptionField = exports.TypeFormTitleField = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const free_solid_svg_icons_1 = require("@fortawesome/free-solid-svg-icons");
const design_system_1 = require("@hashintel/design-system");
const material_1 = require("@mui/material");
const hooks_1 = require("material-ui-popup-state/hooks");
const react_1 = require("react");
const react_hook_form_1 = require("react-hook-form");
const question_icon_1 = require("./question-icon");
const with_handler_1 = require("./with-handler");
const TypeFormTitleField = ({ fieldDisabled, validateTitle, }) => {
    const { register, formState: { isSubmitting, errors: { name: nameError }, }, getValues, clearErrors, } = (0, react_hook_form_1.useFormContext)();
    /**
     * Frustratingly, we have to track this ourselves
     * @see https://github.com/react-hook-form/react-hook-form/discussions/2633
     */
    const [titleValid, setTitleValid] = (0, react_1.useState)(false);
    return ((0, jsx_runtime_1.jsx)(design_system_1.TextField, { fullWidth: true, label: "Singular name", required: true, placeholder: "e.g. Stock Price", disabled: fieldDisabled || isSubmitting, ...(!fieldDisabled && {
            error: !!nameError,
            helperText: nameError?.message,
            success: titleValid,
        }), ...register("name", {
            required: true,
            onChange() {
                clearErrors("name");
                setTitleValid(false);
            },
            async validate(value) {
                if (fieldDisabled) {
                    setTitleValid(true);
                    return true;
                }
                const { allowed, message } = await validateTitle(value);
                setTitleValid(getValues("name") === value && allowed);
                return !allowed ? message : true;
            },
        }) }));
};
exports.TypeFormTitleField = TypeFormTitleField;
// @todo handle this field having a different type than property
const TypeFormDescriptionField = ({ defaultValues, fieldDisabled, }) => {
    const { register, formState: { isSubmitting, errors: { description: descriptionError }, touchedFields: { description: descriptionTouched }, }, clearErrors, } = (0, react_hook_form_1.useFormContext)();
    const [descriptionValid, setDescriptionValid] = (0, react_1.useState)(false);
    /**
     * Some default property types don't have descriptions. We don't want to have
     * to enter one if you pass a preset value for description which is falsey
     *
     * @todo remove this when all property types have descriptions
     */
    const descriptionRequired = !("description" in defaultValues) || !!defaultValues.description;
    return ((0, jsx_runtime_1.jsx)(design_system_1.TextField, { multiline: true, fullWidth: true, inputProps: { minRows: 1 }, label: (0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: ["Description", " ", (0, jsx_runtime_1.jsx)(material_1.Box, { sx: {
                        order: 1,
                        ml: 0.75,
                        display: "flex",
                        alignItems: "center",
                    }, children: (0, jsx_runtime_1.jsx)(question_icon_1.QuestionIcon, { tooltip: "Descriptions help people understand what property types can be used for, and help make them more discoverable (allowing for reuse)." }) })] }), required: descriptionRequired, placeholder: "Describe this property type in one or two sentences", disabled: fieldDisabled || isSubmitting, ...(!fieldDisabled &&
            descriptionTouched && {
            success: descriptionValid,
            error: !!descriptionError,
        }), ...register("description", {
            required: descriptionRequired,
            onChange() {
                clearErrors("description");
                setDescriptionValid(false);
            },
            validate(value) {
                const valid = !descriptionRequired || !!value;
                setDescriptionValid(valid);
                return valid ? true : "You must choose a description";
            },
        }) }));
};
exports.TypeFormDescriptionField = TypeFormDescriptionField;
exports.TypeFormModal = (0, react_1.forwardRef)((props, ref) => {
    const { as = "div", popupState, ...restProps } = props;
    const inner = (0, react_1.createElement)(as, {
        ...restProps,
        ref,
        popupState,
    });
    return ((0, jsx_runtime_1.jsx)(design_system_1.Modal, { ...(0, hooks_1.bindDialog)(popupState), disableEscapeKeyDown: true, contentStyle: (theme) => ({
            p: "0px !important",
            border: 1,
            borderColor: theme.palette.gray[20],
        }), children: inner }));
});
const TypeForm = ({ children, disabledFields = [], getDefaultValues, onClose, modalTitle, popupState, onSubmit, submitButtonProps, validateTitle, }) => {
    const defaultValues = (0, react_1.useMemo)(() => getDefaultValues(), [getDefaultValues]);
    const formMethods = (0, react_hook_form_1.useForm)({
        defaultValues,
        shouldFocusError: true,
        mode: "onSubmit",
        reValidateMode: "onSubmit",
    });
    const { handleSubmit: wrapHandleSubmit, formState: { isSubmitting }, setFocus, } = formMethods;
    const defaultField = defaultValues.name ? "description" : "name";
    (0, react_1.useEffect)(() => {
        setFocus(
        // @ts-expect-error trigger expects Path<T>, but key is already equivalent
        defaultField);
    }, [setFocus, defaultField]);
    const handleSubmit = wrapHandleSubmit(onSubmit);
    return ((0, jsx_runtime_1.jsxs)(react_hook_form_1.FormProvider, { ...formMethods, children: [(0, jsx_runtime_1.jsxs)(material_1.Box, { sx: (theme) => ({
                    px: 2.5,
                    pr: 1.5,
                    pb: 1.5,
                    pt: 2,
                    borderBottom: 1,
                    borderColor: theme.palette.gray[20],
                    alignItems: "center",
                    display: "flex",
                }), children: [(0, jsx_runtime_1.jsx)(material_1.Typography, { variant: "regularTextLabels", sx: { fontWeight: 500, display: "flex", alignItems: "center" }, children: modalTitle }), (0, jsx_runtime_1.jsx)(design_system_1.IconButton, { ...(0, with_handler_1.withHandler)((0, hooks_1.bindToggle)(popupState), onClose), sx: (theme) => ({
                            ml: "auto",
                            svg: {
                                color: theme.palette.gray[50],
                                fontSize: 20,
                            },
                        }), disabled: isSubmitting, children: (0, jsx_runtime_1.jsx)(design_system_1.FontAwesomeIcon, { icon: free_solid_svg_icons_1.faClose }) })] }), (0, jsx_runtime_1.jsxs)(material_1.Box, { minWidth: 500, p: 3, component: "form", display: "block", onSubmit: (event) => {
                    event.preventDefault();
                    event.stopPropagation(); // stop the parent submit being triggered
                    void handleSubmit();
                }, children: [(0, jsx_runtime_1.jsxs)(material_1.Stack, { alignItems: "flex-start", spacing: 3, sx: {
                            [`.${material_1.inputLabelClasses.root}`]: {
                                display: "flex",
                                alignItems: "center",
                            },
                        }, children: [(0, jsx_runtime_1.jsx)(exports.TypeFormTitleField, { fieldDisabled: disabledFields.includes("name"), validateTitle: validateTitle }), (0, jsx_runtime_1.jsx)(exports.TypeFormDescriptionField, { defaultValues: defaultValues, fieldDisabled: disabledFields.includes("description") }), children] }), (0, jsx_runtime_1.jsx)(material_1.Divider, { sx: { mt: 2, mb: 3 } }), (0, jsx_runtime_1.jsxs)(material_1.Stack, { direction: "row", spacing: 1.25, children: [(0, jsx_runtime_1.jsx)(design_system_1.Button, { ...submitButtonProps, loading: isSubmitting, disabled: isSubmitting, type: "submit", size: "small", children: submitButtonProps.children }), (0, jsx_runtime_1.jsx)(design_system_1.Button, { ...(0, with_handler_1.withHandler)((0, hooks_1.bindToggle)(popupState), onClose), disabled: isSubmitting, size: "small", variant: "tertiary", children: "Discard draft" })] })] })] }));
};
exports.TypeForm = TypeForm;
